import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useQueryClient } from '@tanstack/react-query'
import { editChargingCardApiErrors } from './dto/charging-cards-api-errors'
import axios from 'axios'
import { CardStatusDto, useHagerBridgePermissions } from '../../shared'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

export interface EditChargingCardPayload {
  name?: string
  description?: string | null
  card_status?: CardStatusDto
  user_id?: string | null
  expiration_date?: string | null
}

interface EditChargingCardMutationFunctionProps {
  payload: EditChargingCardPayload
  ocpiTokenId: string
  installationId: number
}

interface UseEditChargingCardHook {
  editChargingCard: ({ payload, ocpiTokenId, installationId }: EditChargingCardMutationFunctionProps) => void
  isLoading: boolean
}

/**
 * Function for edit charging card
 * editChargingCardApi mutation is responsible for editing charging card
 * @param payload - data off EditChargingCardFormData added to mutate react-query function
 * @param onClose - function for closing dialog on success method
 * @param installationId - unique installation identifier as a string
 * @returns isLoading as boolean
 * @returns mutate function from react-query
 */
export const useEditChargingCard = (onClose: () => void): UseEditChargingCardHook => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()
  const { permissions } = useHagerBridgePermissions()

  const editChargingCardApi = async ({ payload, ocpiTokenId, installationId }: EditChargingCardMutationFunctionProps): Promise<ApiResult<string>> => {
    const path = permissions.chargingCardPermissions.editChargingCard
      ? `tokens/installation/${installationId}/ocpiTokenId/${ocpiTokenId}`
      : `tokens/installation/${installationId}/ocpiTokenId/${ocpiTokenId}/cardStatus/${payload.card_status}`

    const editChargingCardResponse = authFetch<string>({
      path: path,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'PUT',
      payload: permissions.chargingCardPermissions.editChargingCard && payload,
    })

    return new ApiResult(await editChargingCardResponse)
  }

  const { isPending, mutate: editChargingCard } = usePortalMutation({
    mutationFn: editChargingCardApi,
    onSuccess: () => {
      enqueueSuccessStack(t('emobility.chargingCards.edit.successMessage'))
      queryClient.invalidateQueries({ queryKey: ['chargingCards'] })
      onClose()
    },
    onError: error => {
      if (axios.isAxiosError(error)) {
        enqueueErrorStack(t(editChargingCardApiErrors[error.response?.data.errorCode]))
      } else {
        enqueueErrorStack(t('emobility.chargingCards.edit.errorMessage'))
      }
    },
  })

  return { editChargingCard, isLoading: isPending }
}
